/**
 * MetricCard Component with Growth Indicator and Sparkline
 */
import React from 'react';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: string;
  growth?: number; // percentage change
  sparklineData?: number[];
  color?: 'green' | 'blue' | 'purple' | 'amber' | 'red' | 'gray';
  onClick?: () => void;
  active?: boolean;
  loading?: boolean;
}

const colorClasses = {
  green: { bg: 'from-green-900/50 to-green-800/30', border: 'border-green-700/50', text: 'text-green-400', activeBorder: 'border-green-400 ring-2 ring-green-400/50' },
  blue: { bg: 'from-blue-900/50 to-blue-800/30', border: 'border-blue-700/50', text: 'text-blue-400', activeBorder: 'border-blue-400 ring-2 ring-blue-400/50' },
  purple: { bg: 'from-purple-900/50 to-purple-800/30', border: 'border-purple-700/50', text: 'text-purple-400', activeBorder: 'border-purple-400 ring-2 ring-purple-400/50' },
  amber: { bg: 'from-amber-900/50 to-amber-800/30', border: 'border-amber-700/50', text: 'text-amber-400', activeBorder: 'border-amber-400 ring-2 ring-amber-400/50' },
  red: { bg: 'from-red-900/50 to-red-800/30', border: 'border-red-700/50', text: 'text-red-400', activeBorder: 'border-red-400 ring-2 ring-red-400/50' },
  gray: { bg: 'from-gray-900/50 to-gray-800/30', border: 'border-gray-700/50', text: 'text-white', activeBorder: 'border-gray-400 ring-2 ring-gray-400/50' },
};

// Color hex mapping for sparklines
const sparklineColors: Record<string, string> = {
  'text-green-400': '#4ade80',
  'text-blue-400': '#60a5fa',
  'text-purple-400': '#c084fc',
  'text-amber-400': '#fbbf24',
  'text-red-400': '#f87171',
  'text-white': '#9ca3af',
};

// Simple SVG Sparkline
const Sparkline: React.FC<{ data: number[]; colorClass: string }> = ({ data, colorClass }) => {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const width = 60;
  const height = 20;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`).join(' ');
  const strokeColor = sparklineColors[colorClass] || '#9ca3af';

  return (
    <svg width={width} height={height} className="ml-auto">
      <polyline fill="none" stroke={strokeColor} strokeWidth="1.5" points={points} />
    </svg>
  );
};

// Skeleton loader
const SkeletonCard: React.FC = () => (
  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 animate-pulse">
    <div className="h-3 bg-gray-700 rounded w-20 mb-2" />
    <div className="h-6 bg-gray-700 rounded w-24 mb-1" />
    <div className="h-3 bg-gray-700 rounded w-16" />
  </div>
);

export const MetricCard: React.FC<MetricCardProps> = ({
  title, value, subtitle, icon, growth, sparklineData, color = 'gray', onClick, active, loading
}) => {
  if (loading) return <SkeletonCard />;

  const colors = colorClasses[color];
  const growthColor = growth === undefined ? '' : growth >= 0 ? 'text-green-400' : 'text-red-400';
  const growthArrow = growth === undefined ? '' : growth >= 0 ? '↑' : '↓';

  return (
    <div
      onClick={onClick}
      className={`bg-gradient-to-br ${colors.bg} rounded-lg p-4 border transition-all ${
        onClick ? 'cursor-pointer hover:scale-[1.02]' : ''
      } ${active ? colors.activeBorder : colors.border}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-white text-sm uppercase tracking-wide flex items-center gap-1">
            {icon && <span>{icon}</span>}
            {title}
          </p>
          <p className={`text-xl font-bold ${colors.text} mt-1`}>
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {subtitle && <span className="text-gray-500 text-sm">{subtitle}</span>}
            {growth !== undefined && (
              <span className={`text-sm font-medium ${growthColor}`}>
                {growthArrow} {Math.abs(growth).toFixed(1)}%
              </span>
            )}
          </div>
        </div>
        {sparklineData && sparklineData.length > 1 && (
          <Sparkline data={sparklineData} colorClass={colors.text} />
        )}
      </div>
    </div>
  );
};

export default MetricCard;

